import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { Button, Paper, StepperComponent } from '@roc/ui';
import { observer } from 'mobx-react';
import { createSecureLinkClient } from '@roc-capital/secure-link';
import { useSecureLinkSession } from '@roc-capital/secure-link/react';

import { DecodedData } from './loanFastrackPaymentsUtils';

import LoanFastrackPaymentInformation from './components/loanFastrackPaymentInformation';
import LoanFastrackPayment from './components/loanFastrackPayment';
import LoanFastrackDocusign from './components/loanFastrackDocusign';
import { useLoanFastrackPaymentsStore } from './hooks/useLoanFastrackPaymentsStore';
import { Layout, useBaseStore } from '@roc/feature-app-core';
import { LOAN_FASTRACK, LOAN_FASTRACK_PAYMENT_PURPOSE } from './constants/loanFastrackPayments';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      padding: theme.spacing(3, 0, 4),
      borderRadius: theme.spacing(2),
      boxShadow:
        'rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px',
    },
    stepper: {
      maxWidth: 900,
      margin: 'auto',
      [theme.breakpoints.down('xs')]: {
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
      },
    },
    stepContentWrapper: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      [theme.breakpoints.down('xs')]: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      },
    },
  })
);

/** The Fast Track comes from the session, so the steps get a placeholder instead of an id. */
const SESSION_ENTITY: DecodedData = { entityId: 'secure-link-session', tamariskFlowType: LOAN_FASTRACK };

const Message = ({ title, text, action }: { title: string; text: string; action?: { label: string; onClick: () => void } }) => (
  <Box textAlign="center" py={6} px={2} maxWidth={520} mx="auto">
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Typography color="textSecondary">{text}</Typography>
    {action && (
      <Box mt={3}>
        <Button testId="retry" variant="outlined" color="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      </Box>
    )}
  </Box>
);

const PageTitle = () => (
  <Box my={3}>
    <Typography variant="h3" gutterBottom>
      {'Loan Fast Track Payment'}
    </Typography>
  </Box>
);

/**
 * The Fast Track payment page for secure links: /link/loan-fastrack-payment. The borrower arrives here from the
 * platform landing page (/platform-app/link#token), which started the session; every call is authorized by that
 * session, and the Fast Track comes from it on the server. Replaces /loan-fastrack-payments/:encodedParams, which
 * carried the Fast Track id in base64.
 */
export const SecureLinkLoanFastrackPaymentPage = observer(() => {
  const classes = useStyles();
  const { environmentStore } = useBaseStore();
  const client = useMemo(
    () => createSecureLinkClient({ apiBaseUrl: environmentStore.environmentVariables.restApiBaseURL ?? '' }),
    []
  );
  const { status, request, retry } = useSecureLinkSession(client, { purpose: LOAN_FASTRACK_PAYMENT_PURPOSE });
  const { loanFastrackPaymentsStore } = useLoanFastrackPaymentsStore();

  // A layout effect, so it runs before the steps' own effects (the first data fetch) on the same commit.
  useLayoutEffect(() => loanFastrackPaymentsStore.setRequest(request), [loanFastrackPaymentsStore, request]);

  return (
    <Layout>
      <PageTitle />
      <Paper className={classes.paper}>
        {status === 'loading' && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {status === 'ended' && (
          <Message
            title="Your session has ended"
            text="For your security, sessions end after 15 minutes or when the link is opened in another window. Open the link from your email again to continue."
          />
        )}
        {status === 'unavailable' && (
          <Message
            title="We couldn't open your payment"
            text="This is usually temporary. Check your connection and try again."
            action={{ label: 'Try again', onClick: retry }}
          />
        )}
        {status === 'active' && <LoanFastrackPaymentsBody entityData={SESSION_ENTITY} />}
      </Paper>
    </Layout>
  );
});

/**
 * /loan-fastrack-payments/:encodedParams, the old Fast Track link format (the Fast Track id in base64). Those links no
 * longer work: the lender sends a new one, which opens /link/loan-fastrack-payment.
 */
export const LoanFastrackLinkReplacedPage = () => {
  const classes = useStyles();
  return (
    <Layout>
      <PageTitle />
      <Paper className={classes.paper}>
        <Message
          title="This link is no longer valid"
          text="Please ask your lender to send you a new link to pay the Fast Track fee."
        />
      </Paper>
    </Layout>
  );
};

/**
 * The Fast Track payment steps. Only the secure link page (/link/loan-fastrack-payment) renders them: every call is
 * authorized by its session, and the Fast Track comes from the session on the server.
 */
export const LoanFastrackPaymentsBody = observer(
  ({ entityData }: { entityData: DecodedData }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [disableStepperNavigation, setDisableStepperNavigation] = useState(
      false
    );
    const { entityId, tamariskFlowType } = entityData;
    const { loanFastrackPaymentsStore } = useLoanFastrackPaymentsStore();
    const { docusignStatus } = loanFastrackPaymentsStore;
    const classes = useStyles();

    const loanTermsSteps = [
      {
        title: 'Payment Details',
        icon: null,
        onBack: () => {},
        onNext: () => {},
        allowBack: false,
        allowNext: true,
      },
      {
        title: 'Acknowledgment',
        icon: null,
        onBack: () => {},
        onNext: () => {},
        allowBack: true,
        allowNext: true,
      },
      {
        title: 'Payment',
        icon: null,
        onBack: () => {},
        onNext: () => {},
        allowBack: false,
        allowNext: true,
      },
    ];

    const handleBackStep = () => setActiveStep(activeStep - 1);

    const handleNextStep = () => setActiveStep(activeStep + 1);

    const handleStep = (index: number) => setActiveStep(index);

    useEffect(() => {
      return () => {
        loanFastrackPaymentsStore.reset();
        loanFastrackPaymentsStore.loanFastrackDocumentStore.reset();
      };
    }, []);

    useEffect(() => {
      if (entityId) {
        loanFastrackPaymentsStore.fetchDocusignAndPaymentStatus();
      }
    }, [entityId]);

    useEffect(() => {
      setDisableStepperNavigation(!docusignStatus);
    }, [docusignStatus]);

    if (entityId) {
      return (
        <>
          <StepperComponent
            activeStep={activeStep}
            disableNavigation={disableStepperNavigation}
            handleStep={handleStep}
            steps={loanTermsSteps}
            className={classes.stepper}
          />
          <Box mt={3} mx={'auto'} className={classes.stepContentWrapper}>
            {activeStep === 0 && (
              <LoanFastrackPaymentInformation
                handleNextStep={handleNextStep}
                entityId={entityId}
                tamariskFlowType={tamariskFlowType}
              />
            )}
            {activeStep === 1 && (
              <LoanFastrackDocusign
                handleBackStep={handleBackStep}
                handleNextStep={handleNextStep}
                entityId={entityId}
                tamariskFlowType={tamariskFlowType}
              />
            )}
            {activeStep === 2 && (
              <LoanFastrackPayment
                handleDisableStepperNavigation={setDisableStepperNavigation}
                entityData={entityData}
              />
            )}
          </Box>
        </>
      );
    } else {
      return null;
    }
  }
);
